/**
 * Cuisine Color Mapping for Taste Aura
 *
 * Maps cuisine types to color temperatures for dynamic gradient backgrounds.
 * Used to generate personalized "Taste Aura" gradients based on user's favorite cuisines.
 */

export type ColorTemperature = 'warm' | 'cool' | 'earthy' | 'vibrant'

export interface CuisineColorInfo {
  temperature: ColorTemperature
  primaryColor: string
  secondaryColor: string
  accentColor: string
}

// Map cuisine names to color temperatures and specific colors
// Based on the app's quiz cuisine options - VIBRANT edition
const CUISINE_COLOR_MAP: Record<string, CuisineColorInfo> = {
  // ============================================================
  // WARM CUISINES (spicy, bold flavors) - vivid oranges, reds, fiery tones
  // ============================================================
  indian: {
    temperature: 'warm',
    primaryColor: '#FF6D00', // Vivid saffron
    secondaryColor: '#FF1744', // Bright red
    accentColor: '#FFD600', // Turmeric gold
  },
  thai: {
    temperature: 'warm',
    primaryColor: '#FF3D00', // Hot orange
    secondaryColor: '#FFAB00', // Thai gold
    accentColor: '#00E676', // Thai basil green
  },
  korean: {
    temperature: 'warm',
    primaryColor: '#FF1744', // Gochujang red
    secondaryColor: '#FF6D00', // Kimchi orange
    accentColor: '#FFEA00', // Bright yellow
  },
  'korean-bbq': {
    temperature: 'warm',
    primaryColor: '#FF3D00', // Grilled flame
    secondaryColor: '#DD2C00', // Charred red
    accentColor: '#FFAB00', // Caramelized gold
  },
  mexican: {
    temperature: 'warm',
    primaryColor: '#FF1744', // Chili red
    secondaryColor: '#00E676', // Lime green
    accentColor: '#FFEA00', // Marigold yellow
  },
  'tex-mex': {
    temperature: 'warm',
    primaryColor: '#FF6D00', // Texas sunset
    secondaryColor: '#FF1744', // Salsa red
    accentColor: '#FFEA00', // Cheese yellow
  },
  'ethiopian-eritrean': {
    temperature: 'warm',
    primaryColor: '#00E676', // Ethiopian green
    secondaryColor: '#FF1744', // Ethiopian red
    accentColor: '#FFEA00', // Ethiopian gold
  },
  'jamaican-jerk': {
    temperature: 'warm',
    primaryColor: '#FFEA00', // Jamaican gold
    secondaryColor: '#00E676', // Jamaican green
    accentColor: '#FF6D00', // Scotch bonnet orange
  },
  caribbean: {
    temperature: 'warm',
    primaryColor: '#00E5FF', // Caribbean turquoise
    secondaryColor: '#FFEA00', // Tropical yellow
    accentColor: '#FF6D00', // Sunset orange
  },
  creole: {
    temperature: 'warm',
    primaryColor: '#FF3D00', // Cayenne orange
    secondaryColor: '#7C4DFF', // Mardi Gras purple
    accentColor: '#FFEA00', // Gold
  },
  haitian: {
    temperature: 'warm',
    primaryColor: '#2979FF', // Haitian blue
    secondaryColor: '#FF1744', // Haitian red
    accentColor: '#FFEA00', // Accent gold
  },
  'west-african': {
    temperature: 'warm',
    primaryColor: '#FF6D00', // Palm oil orange
    secondaryColor: '#00E676', // Forest green
    accentColor: '#FFEA00', // Bright gold
  },
  pakistani: {
    temperature: 'warm',
    primaryColor: '#00E676', // Pakistani green
    secondaryColor: '#FF6D00', // Spice orange
    accentColor: '#FFEA00', // Gold accent
  },
  nepalese: {
    temperature: 'warm',
    primaryColor: '#FF1744', // Nepali crimson
    secondaryColor: '#2979FF', // Mountain blue
    accentColor: '#FFEA00', // Temple gold
  },
  burmese: {
    temperature: 'warm',
    primaryColor: '#FFEA00', // Burmese gold
    secondaryColor: '#00E676', // Jade green
    accentColor: '#FF6D00', // Turmeric
  },
  cambodian: {
    temperature: 'warm',
    primaryColor: '#2979FF', // Cambodian blue
    secondaryColor: '#FF1744', // Temple red
    accentColor: '#FFEA00', // Gold
  },

  // ============================================================
  // COOL CUISINES (fresh, light, seafood) - electric blues, aquas, cyans
  // ============================================================
  'japanese-sushi': {
    temperature: 'cool',
    primaryColor: '#00B0FF', // Ocean blue
    secondaryColor: '#1DE9B6', // Wasabi green
    accentColor: '#FF1744', // Tuna red
  },
  hawaiian: {
    temperature: 'cool',
    primaryColor: '#00E5FF', // Pacific cyan
    secondaryColor: '#FF6D00', // Tropical orange
    accentColor: '#FFEA00', // Pineapple yellow
  },
  greek: {
    temperature: 'cool',
    primaryColor: '#2979FF', // Aegean blue
    secondaryColor: '#FFFFFF', // White
    accentColor: '#00E5FF', // Mediterranean cyan
  },
  'scandinavian-nordic': {
    temperature: 'cool',
    primaryColor: '#536DFE', // Nordic blue
    secondaryColor: '#B388FF', // Aurora purple
    accentColor: '#00E5FF', // Ice cyan
  },
  swedish: {
    temperature: 'cool',
    primaryColor: '#2979FF', // Swedish blue
    secondaryColor: '#FFEA00', // Swedish yellow
    accentColor: '#536DFE', // Accent
  },
  scottish: {
    temperature: 'cool',
    primaryColor: '#304FFE', // Scottish royal blue
    secondaryColor: '#00E676', // Highland green
    accentColor: '#B388FF', // Thistle purple
  },
  peruvian: {
    temperature: 'cool',
    primaryColor: '#FF1744', // Peruvian red
    secondaryColor: '#00E5FF', // Ceviche cyan
    accentColor: '#FFEA00', // Inca gold
  },
  samoan: {
    temperature: 'cool',
    primaryColor: '#00B0FF', // Pacific blue
    secondaryColor: '#00E5FF', // Ocean cyan
    accentColor: '#1DE9B6', // Reef green
  },

  // ============================================================
  // EARTHY CUISINES (rustic, traditional) - rich, saturated naturals
  // ============================================================
  french: {
    temperature: 'earthy',
    primaryColor: '#304FFE', // French blue
    secondaryColor: '#FF1744', // French red
    accentColor: '#FFEA00', // Beurre gold
  },
  italian: {
    temperature: 'earthy',
    primaryColor: '#00E676', // Basil green
    secondaryColor: '#FF1744', // Tomato red
    accentColor: '#FFEA00', // Pasta gold
  },
  german: {
    temperature: 'earthy',
    primaryColor: '#FF6D00', // Beer amber
    secondaryColor: '#FFEA00', // Mustard yellow
    accentColor: '#795548', // Pretzel brown
  },
  american: {
    temperature: 'earthy',
    primaryColor: '#2979FF', // American blue
    secondaryColor: '#FF1744', // American red
    accentColor: '#FFEA00', // Gold
  },
  'new-american': {
    temperature: 'earthy',
    primaryColor: '#00E676', // Farm green
    secondaryColor: '#FF6D00', // Harvest orange
    accentColor: '#7C4DFF', // Modern purple
  },
  'american-bbq': {
    temperature: 'earthy',
    primaryColor: '#FF3D00', // Smoke orange
    secondaryColor: '#DD2C00', // BBQ red
    accentColor: '#FFAB00', // Honey gold
  },
  belgian: {
    temperature: 'earthy',
    primaryColor: '#FF6D00', // Beer amber
    secondaryColor: '#FFEA00', // Belgian gold
    accentColor: '#795548', // Chocolate brown
  },
  polish: {
    temperature: 'earthy',
    primaryColor: '#FF1744', // Polish red
    secondaryColor: '#FFFFFF', // Polish white
    accentColor: '#FFEA00', // Amber
  },
  hungarian: {
    temperature: 'earthy',
    primaryColor: '#FF1744', // Paprika red
    secondaryColor: '#00E676', // Green pepper
    accentColor: '#FFEA00', // Gold
  },
  romanian: {
    temperature: 'earthy',
    primaryColor: '#2979FF', // Romanian blue
    secondaryColor: '#FFEA00', // Romanian yellow
    accentColor: '#FF1744', // Romanian red
  },
  russian: {
    temperature: 'earthy',
    primaryColor: '#2979FF', // Russian blue
    secondaryColor: '#FF1744', // Russian red
    accentColor: '#FFEA00', // Gold dome
  },
  ukrainian: {
    temperature: 'earthy',
    primaryColor: '#2979FF', // Ukrainian blue
    secondaryColor: '#FFEA00', // Ukrainian yellow
    accentColor: '#00E676', // Sunflower
  },
  portuguese: {
    temperature: 'earthy',
    primaryColor: '#00E676', // Portuguese green
    secondaryColor: '#FF1744', // Portuguese red
    accentColor: '#FFEA00', // Gold
  },
  croatian: {
    temperature: 'earthy',
    primaryColor: '#FF1744', // Croatian red
    secondaryColor: '#2979FF', // Adriatic blue
    accentColor: '#FFFFFF', // White
  },
  albanian: {
    temperature: 'earthy',
    primaryColor: '#FF1744', // Albanian red
    secondaryColor: '#212121', // Black eagle
    accentColor: '#FFEA00', // Gold
  },
  armenian: {
    temperature: 'earthy',
    primaryColor: '#FF1744', // Armenian red
    secondaryColor: '#2979FF', // Armenian blue
    accentColor: '#FF6D00', // Apricot orange
  },
  danish: {
    temperature: 'earthy',
    primaryColor: '#FF1744', // Danish red
    secondaryColor: '#FFFFFF', // White
    accentColor: '#FFEA00', // Gold
  },
  'home-style-comfort-food': {
    temperature: 'earthy',
    primaryColor: '#FFAB00', // Warm gold
    secondaryColor: '#FF6D00', // Comfort orange
    accentColor: '#8D6E63', // Cozy brown
  },
  'soul-food-southern-cuisine': {
    temperature: 'earthy',
    primaryColor: '#FF6D00', // Sweet potato orange
    secondaryColor: '#00E676', // Collard green
    accentColor: '#FFAB00', // Cornbread gold
  },
  jewish: {
    temperature: 'earthy',
    primaryColor: '#2979FF', // Israeli blue
    secondaryColor: '#FFFFFF', // White
    accentColor: '#FFEA00', // Gold
  },
  argentinian: {
    temperature: 'earthy',
    primaryColor: '#00B0FF', // Argentina blue
    secondaryColor: '#FFFFFF', // White
    accentColor: '#FFEA00', // Sun gold
  },
  spanish: {
    temperature: 'earthy',
    primaryColor: '#FF1744', // Spanish red
    secondaryColor: '#FFEA00', // Spanish gold
    accentColor: '#FF6D00', // Saffron
  },

  // ============================================================
  // VIBRANT CUISINES (bold, colorful, festive) - electric, neon tones
  // ============================================================
  'traditional-chinese': {
    temperature: 'vibrant',
    primaryColor: '#FF1744', // Lucky red
    secondaryColor: '#FFD600', // Imperial gold
    accentColor: '#00E676', // Jade green
  },
  'american-chinese': {
    temperature: 'vibrant',
    primaryColor: '#FF3D00', // Sweet & sour orange
    secondaryColor: '#FFD600', // Fortune gold
    accentColor: '#FF1744', // Red
  },
  vietnamese: {
    temperature: 'vibrant',
    primaryColor: '#FF3D00', // Sriracha orange
    secondaryColor: '#FFEA00', // Star anise yellow
    accentColor: '#00E676', // Herb green
  },
  filipino: {
    temperature: 'vibrant',
    primaryColor: '#2979FF', // Filipino blue
    secondaryColor: '#FF1744', // Filipino red
    accentColor: '#FFD600', // Filipino sun
  },
  indonesian: {
    temperature: 'vibrant',
    primaryColor: '#FF1744', // Indonesian red
    secondaryColor: '#FFFFFF', // White
    accentColor: '#FFD600', // Gold
  },
  malaysian: {
    temperature: 'vibrant',
    primaryColor: '#2979FF', // Malaysian blue
    secondaryColor: '#FFD600', // Malaysian gold
    accentColor: '#FF1744', // Red
  },
  turkish: {
    temperature: 'vibrant',
    primaryColor: '#FF1744', // Turkish red
    secondaryColor: '#00E5FF', // Turkish tile blue
    accentColor: '#FFD600', // Gold
  },
  lebanese: {
    temperature: 'vibrant',
    primaryColor: '#FF1744', // Lebanese red
    secondaryColor: '#00E676', // Cedar green
    accentColor: '#FFD600', // Gold
  },
  moroccan: {
    temperature: 'vibrant',
    primaryColor: '#FF3D00', // Moroccan orange
    secondaryColor: '#7C4DFF', // Royal purple
    accentColor: '#FFD600', // Spice gold
  },
  syrian: {
    temperature: 'vibrant',
    primaryColor: '#FF1744', // Syrian red
    secondaryColor: '#00E676', // Green
    accentColor: '#FFD600', // Gold
  },
  israeli: {
    temperature: 'vibrant',
    primaryColor: '#2979FF', // Israeli blue
    secondaryColor: '#FFFFFF', // White
    accentColor: '#FFD600', // Gold
  },
  'iranian-persian': {
    temperature: 'vibrant',
    primaryColor: '#00E676', // Persian green
    secondaryColor: '#FF1744', // Red
    accentColor: '#FFD600', // Saffron gold
  },
  egyptian: {
    temperature: 'vibrant',
    primaryColor: '#FFD600', // Egyptian gold
    secondaryColor: '#00E5FF', // Nile blue
    accentColor: '#FF1744', // Red
  },
  cuban: {
    temperature: 'vibrant',
    primaryColor: '#2979FF', // Cuban blue
    secondaryColor: '#FF1744', // Cuban red
    accentColor: '#FFD600', // Havana gold
  },
  dominican: {
    temperature: 'vibrant',
    primaryColor: '#2979FF', // Dominican blue
    secondaryColor: '#FF1744', // Dominican red
    accentColor: '#FFD600', // Gold
  },
  venezuelan: {
    temperature: 'vibrant',
    primaryColor: '#FFD600', // Venezuelan yellow
    secondaryColor: '#2979FF', // Venezuelan blue
    accentColor: '#FF1744', // Venezuelan red
  },
  guatemalan: {
    temperature: 'vibrant',
    primaryColor: '#00B0FF', // Guatemalan blue
    secondaryColor: '#FFFFFF', // White
    accentColor: '#FFD600', // Gold
  },
  honduran: {
    temperature: 'vibrant',
    primaryColor: '#00B0FF', // Honduran blue
    secondaryColor: '#FFFFFF', // White
    accentColor: '#FFD600', // Gold
  },
  mongolian: {
    temperature: 'vibrant',
    primaryColor: '#FF1744', // Mongolian red
    secondaryColor: '#2979FF', // Sky blue
    accentColor: '#FFD600', // Gold
  },
  'asian-fusion': {
    temperature: 'vibrant',
    primaryColor: '#7C4DFF', // Electric purple
    secondaryColor: '#FF3D00', // Neon orange
    accentColor: '#00E5FF', // Cyber cyan
  },
}

// Default colors for unknown cuisines
const DEFAULT_COLOR_INFO: CuisineColorInfo = {
  temperature: 'vibrant',
  primaryColor: '#7C3AED', // Paire purple
  secondaryColor: '#FF6B35', // Paire orange
  accentColor: '#6D28D9',
}

// Temperature-based color palettes for mixing
const TEMPERATURE_PALETTES: Record<ColorTemperature, { colors: string[]; gradient: string }> = {
  warm: {
    colors: ['#E74C3C', '#FF6B35', '#F39C12', '#E65100', '#D35400'],
    gradient: 'linear-gradient(135deg, #E74C3C 0%, #FF6B35 50%, #F39C12 100%)',
  },
  cool: {
    colors: ['#2196F3', '#00BCD4', '#4DD0E1', '#0097A7', '#26C6DA'],
    gradient: 'linear-gradient(135deg, #2196F3 0%, #00BCD4 50%, #4DD0E1 100%)',
  },
  earthy: {
    colors: ['#4CAF50', '#8BC34A', '#795548', '#6D4C41', '#558B2F'],
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 50%, #795548 100%)',
  },
  vibrant: {
    colors: ['#7C3AED', '#F44336', '#FFC107', '#FF5722', '#E91E63'],
    gradient: 'linear-gradient(135deg, #7C3AED 0%, #F44336 50%, #FFC107 100%)',
  },
}

/**
 * Normalize a cuisine name for lookup
 * Handles quiz format like "Jamaican / Jerk" → "jamaican-jerk"
 */
function normalizeCuisineName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\?$/, '') // Remove trailing question mark
    .replace(/\s*\/\s*/g, '-') // Convert " / " to "-"
    .replace(/\s+/g, '-') // Convert remaining spaces to hyphens
}

/**
 * Get the color temperature for a cuisine
 */
export function getCuisineColorTemperature(cuisine: string): ColorTemperature {
  const normalized = normalizeCuisineName(cuisine)
  return CUISINE_COLOR_MAP[normalized]?.temperature ?? 'vibrant'
}

/**
 * Get full color info for a cuisine
 */
export function getCuisineColorInfo(cuisine: string): CuisineColorInfo {
  const normalized = normalizeCuisineName(cuisine)
  return CUISINE_COLOR_MAP[normalized] ?? DEFAULT_COLOR_INFO
}

/**
 * Interface for favorite cuisine (matches the app's type)
 */
export interface FavoriteCuisine {
  name: string
  flag?: string
}

/**
 * Generate a Taste Aura gradient based on user's favorite cuisines
 * Blends colors from multiple cuisines weighted by their position
 */
export function generateTasteAuraGradient(cuisines: FavoriteCuisine[]): string {
  if (!cuisines || cuisines.length === 0) {
    // Default Paire gradient
    return 'linear-gradient(135deg, rgba(124, 58, 237, 0.3) 0%, rgba(255, 107, 53, 0.3) 50%, rgba(42, 157, 143, 0.3) 100%)'
  }

  // Get colors from top cuisines (max 3 for gradient)
  const topCuisines = cuisines.slice(0, 3)
  const colors = topCuisines.map((c) => getCuisineColorInfo(c.name))

  if (colors.length === 1) {
    // Single cuisine - use its colors with opacity
    const c = colors[0]
    return `linear-gradient(135deg, ${hexToRgba(c.primaryColor, 0.4)} 0%, ${hexToRgba(c.secondaryColor, 0.35)} 50%, ${hexToRgba(c.accentColor, 0.3)} 100%)`
  }

  if (colors.length === 2) {
    // Two cuisines - blend them
    return `linear-gradient(135deg, ${hexToRgba(colors[0].primaryColor, 0.4)} 0%, ${hexToRgba(colors[0].secondaryColor, 0.35)} 33%, ${hexToRgba(colors[1].primaryColor, 0.35)} 66%, ${hexToRgba(colors[1].secondaryColor, 0.3)} 100%)`
  }

  // Three or more cuisines - use primary colors from each
  return `linear-gradient(135deg, ${hexToRgba(colors[0].primaryColor, 0.4)} 0%, ${hexToRgba(colors[1].primaryColor, 0.35)} 50%, ${hexToRgba(colors[2].primaryColor, 0.3)} 100%)`
}

/**
 * Generate CSS keyframes for animated Taste Aura
 * Returns the animation name and keyframes CSS
 */
export function generateAnimatedGradientKeyframes(cuisines: FavoriteCuisine[]): {
  animationName: string
  keyframes: string
} {
  const colors = cuisines.slice(0, 3).map((c) => getCuisineColorInfo(c.name))

  if (colors.length === 0) {
    colors.push(DEFAULT_COLOR_INFO)
  }

  const animationName = 'tasteAuraShift'

  // Create a shifting gradient animation
  const primaryColors = colors.map((c) => hexToRgba(c.primaryColor, 0.4))
  const secondaryColors = colors.map((c) => hexToRgba(c.secondaryColor, 0.35))

  const keyframes = `
@keyframes ${animationName} {
  0% {
    background-position: 0% 50%;
    filter: hue-rotate(0deg);
  }
  25% {
    background-position: 50% 100%;
    filter: hue-rotate(5deg);
  }
  50% {
    background-position: 100% 50%;
    filter: hue-rotate(0deg);
  }
  75% {
    background-position: 50% 0%;
    filter: hue-rotate(-5deg);
  }
  100% {
    background-position: 0% 50%;
    filter: hue-rotate(0deg);
  }
}`

  return { animationName, keyframes }
}

/**
 * Get the dominant color temperature from a list of cuisines
 */
export function getDominantTemperature(cuisines: FavoriteCuisine[]): ColorTemperature {
  if (!cuisines || cuisines.length === 0) return 'vibrant'

  const temperatureCounts: Record<ColorTemperature, number> = {
    warm: 0,
    cool: 0,
    earthy: 0,
    vibrant: 0,
  }

  cuisines.forEach((c) => {
    const temp = getCuisineColorTemperature(c.name)
    temperatureCounts[temp]++
  })

  let dominantTemp: ColorTemperature = 'vibrant'
  let maxCount = 0

  ;(Object.keys(temperatureCounts) as ColorTemperature[]).forEach((temp) => {
    if (temperatureCounts[temp] > maxCount) {
      maxCount = temperatureCounts[temp]
      dominantTemp = temp
    }
  })

  return dominantTemp
}

/**
 * Get the temperature palette for a given temperature
 */
export function getTemperaturePalette(temperature: ColorTemperature) {
  return TEMPERATURE_PALETTES[temperature]
}

/**
 * Convert hex color to rgba
 */
export function hexToRgba(hex: string, alpha: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return `rgba(124, 58, 237, ${alpha})` // Fallback to purple

  const r = parseInt(result[1], 16)
  const g = parseInt(result[2], 16)
  const b = parseInt(result[3], 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Get a solid color (no transparency) for the dominant cuisine
 * Useful for borders, accents, etc.
 */
export function getDominantCuisineColor(cuisines: FavoriteCuisine[]): string {
  if (!cuisines || cuisines.length === 0) return '#7C3AED' // Paire purple

  const colorInfo = getCuisineColorInfo(cuisines[0].name)
  return colorInfo.primaryColor
}

/**
 * Generate a glow shadow based on cuisine colors
 */
export function generateCuisineGlow(cuisines: FavoriteCuisine[], intensity: number = 0.3): string {
  const color = getDominantCuisineColor(cuisines)
  const rgba = hexToRgba(color, intensity)
  return `0 0 30px ${rgba}, 0 0 60px ${hexToRgba(color, intensity * 0.5)}`
}

/**
 * Calculate relative luminance of a hex color (0-1)
 * Used for determining text contrast
 * Based on WCAG 2.0 formula
 */
function getRelativeLuminance(hex: string): number {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return 0.5

  const [r, g, b] = [result[1], result[2], result[3]].map((c) => {
    const sRGB = parseInt(c, 16) / 255
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4)
  })

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Darken a hex color by a factor (0-1, where 0 = black, 1 = original)
 */
function darkenColor(hex: string, factor: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return hex

  const r = Math.round(parseInt(result[1], 16) * factor)
  const g = Math.round(parseInt(result[2], 16) * factor)
  const b = Math.round(parseInt(result[3], 16) * factor)

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

export interface CuisineChipStyle {
  /** Gradient background for the chip */
  gradient: string
  /** Text color (white or dark) based on background luminance */
  textColor: string
  /** Text shadow for better readability */
  textShadow: string
  /** Primary color for accents */
  primaryColor: string
}

/**
 * Generate chip styling for a cuisine with proper text contrast
 * Automatically determines if text should be light or dark based on background luminance
 */
export function generateCuisineChipStyle(cuisineName: string): CuisineChipStyle {
  const colorInfo = getCuisineColorInfo(cuisineName)

  // Create a gradient from primary to secondary, slightly darkened for better text contrast
  const primary = darkenColor(colorInfo.primaryColor, 0.85)
  const secondary = darkenColor(colorInfo.secondaryColor, 0.8)
  const gradient = `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`

  // Calculate average luminance of the gradient colors
  const avgLuminance = (getRelativeLuminance(primary) + getRelativeLuminance(secondary)) / 2

  // Use dark text if background is light (luminance > 0.4)
  const isLightBackground = avgLuminance > 0.4
  const textColor = isLightBackground ? 'gray.900' : 'white'
  const textShadow = isLightBackground
    ? 'none'
    : '0 1px 2px rgba(0, 0, 0, 0.3)'

  return {
    gradient,
    textColor,
    textShadow,
    primaryColor: colorInfo.primaryColor,
  }
}
